import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  Droplets,
  CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ea404883-7061-4aba-83d0-2d4ab5ad0223/artifacts/ffswoxpp_69d6c27f-288c-44ba-8ef2-7664150a7535.jpeg";

// 3D Contact Card
const ContactCard3D = ({ icon: Icon, title, content, subContent, href, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -15 : 15, 0, index % 2 === 0 ? 5 : -5]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [50, 0, 0, -30]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.95]);

  const Component = href ? 'a' : 'div';
  const props = href ? { href } : {};

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateY,
        y,
        scale,
        transformStyle: 'preserve-3d',
      }}
    >
      <Component 
        {...props}
        className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl bg-sky-50 hover:bg-sky-100 transition-all group"
      >
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-sky-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">{title}</div>
          <div className="text-sky-600 font-medium text-sm sm:text-base truncate">{content}</div>
          {subContent && <div className="text-xs sm:text-sm text-slate-500 mt-0.5">{subContent}</div>}
        </div>
      </Component>
    </motion.div>
  );
};

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const headerY = useTransform(scrollYProgress, [0, 0.2], [0, -30]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.8]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 5000);
  };

  return (
    <main ref={containerRef} className="pt-16 sm:pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-sky-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-sky-100 rounded-full blur-3xl opacity-50" />
          <div className="absolute bottom-10 left-10 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-100 rounded-full blur-3xl opacity-40" />
        </div>

        <motion.div 
          style={{ y: headerY, opacity: headerOpacity }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6"
          >
            <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
            Contacto
          </motion.span>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-slate-900 mb-4 sm:mb-6 tracking-tight px-4"
          >
            Hablemos de tu{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-500">
              proyecto
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4"
          >
            Estamos aquí para ayudarte. Te responderemos lo antes posible.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <div className="mb-8 sm:mb-10 lg:mb-12">
                <img 
                  src={LOGO_URL}
                  alt="Piscinas Genovés"
                  className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-contain rounded-xl sm:rounded-2xl mb-4 sm:mb-6"
                />
                <h2 className="font-manrope font-bold text-xl sm:text-2xl text-slate-900 mb-2 sm:mb-4">
                  Piscinas Genovés
                </h2>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Tu experto en piscinas en la Costa Brava. 
                  Más de 15 años de experiencia nos avalan.
                </p>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <ContactCard3D
                  icon={Phone}
                  title="Teléfono"
                  content="+34 634 06 08 78"
                  subContent="Llámanos"
                  href="tel:+34634060878"
                  index={0}
                />
                <ContactCard3D
                  icon={Mail}
                  title="Email"
                  content="genovespiscinas@gmail.com"
                  subContent="Te respondemos en 24h"
                  href="mailto:genovespiscinas@gmail.com"
                  index={1}
                />
                <ContactCard3D
                  icon={MapPin}
                  title="Dirección"
                  content="Puigmal 41/44 Local 3"
                  subContent="Empuriabrava 17487"
                  index={2}
                />
                <ContactCard3D
                  icon={Clock}
                  title="Horario"
                  content="Lun - Vie: 9:00 - 19:00"
                  subContent="Sáb: 9:00 - 14:00"
                  index={3}
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-xl border border-slate-100">
                <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-900 mb-4 sm:mb-6">
                  Envíanos un mensaje
                </h3>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8 sm:py-12"
                  >
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
                    </div>
                    <h4 className="font-manrope font-bold text-lg sm:text-xl text-slate-900 mb-2">
                      ¡Mensaje enviado!
                    </h4>
                    <p className="text-slate-600 text-sm sm:text-base">
                      Te responderemos lo antes posible.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 lg:space-y-6" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                        Nombre completo
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all text-sm sm:text-base"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="input-email"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all text-sm sm:text-base"
                          placeholder="tu@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          data-testid="input-phone"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all text-sm sm:text-base"
                          placeholder="+34 600 000 000"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                        Mensaje
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        data-testid="input-message"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all resize-none text-sm sm:text-base"
                        placeholder="¿En qué podemos ayudarte?"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="submit-button"
                      className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all hover:scale-[1.02] shadow-lg shadow-sky-500/25"
                    >
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      Enviar mensaje
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="font-manrope font-bold text-xl sm:text-2xl text-slate-900 mb-2 sm:mb-4">
              Encuéntranos
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Puigmal 41/44 Local 3, Empuriabrava 17487
            </p>
          </div>

          <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl h-[300px] sm:h-[350px] lg:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2976.3!2d3.1167!3d42.2567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDE1JzI0LjEiTiAzwrAwNyczOS41IkU!5e0!3m2!1ses!2ses!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Ubicación"
              data-testid="map-iframe"
            />
          </div>
        </div>
      </section>

      {/* Quick CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: 10 + Math.random() * 30,
                height: 10 + Math.random() * 30,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{ y: [0, -20, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-manrope font-extrabold text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-white mb-4 sm:mb-6">
            ¿Prefieres llamarnos?
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-sky-100 mb-6 sm:mb-8">
            Lunes a viernes de 9:00 a 19:00, sábados de 9:00 a 14:00.
          </p>
          <a
            href="tel:+34634060878"
            data-testid="quick-call-btn"
            className="inline-flex items-center gap-2 sm:gap-3 bg-white text-sky-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all hover:scale-105 shadow-xl"
          >
            <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
            +34 634 06 08 78
          </a>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
